import {useState, useEffect, useMemo, useCallback} from 'react';
import {ServicesState, YClientsFormState} from '@target-web/leadads-form-view';
import {useSearchParams} from 'utils/searchParamsContext';
import {fetchCheckServices, fetchServices} from 'utils/requests';

type useServicesProps = {
    staffId: YClientsFormState['staffId'];
    datetime: string;
    selectedServices: number[];
    active?: boolean;
    setSelectedServices: (s: number[]) => void;
};

export const useServices = ({staffId, datetime, active, selectedServices, setSelectedServices}: useServicesProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [services, setServices] = useState<ServicesState['services']>([]);
    const [categories, setCategories] = useState<ServicesState['categories']>([]);
    const {formDataHash} = useSearchParams();
    const [activeServices, setActiveServices] = useState<number[]>([]);

    const params = useMemo(
        () => ({
            ...(staffId ? {staff_id: String(staffId)} : {}),
            ...(datetime ? {datetime: datetime.slice(0, 19)} : {}),
            lead_form_data: formDataHash,
        }),
        [staffId, datetime, formDataHash],
    );

    const getCompatibleServices = useCallback(
        (selectedServicesProp?: number[]) => {
            const servicesStr = (selectedServicesProp || selectedServices).reduce((res, cur, i) => {
                return i ? res + `&service_ids[]=${cur}` : res + `service_ids[]=${cur}`;
            }, '');

            return fetchCheckServices(`${servicesStr}&lead_form_data=${formDataHash}`);
        },
        [formDataHash, selectedServices],
    );

    const updateAwailableState = useCallback(async () => {
        setIsLoading(true);
        // идем за услугами
        const {data} = await fetchServices(new URLSearchParams(Object.entries(params)).toString());

        if (!data.success) {
            // TODO ошибка
            return;
        }

        const services = data.data.services;
        setServices(services.sort((a, b) => b.weight - a.weight));
        // в апи yclients неожиданность, что если нет доступных услуг - возвращаются все категории
        setCategories(services.length ? data.data.category.sort((a, b) => b.weight - a.weight) : []);

        // смотрим есть ли все сервисы, которые мы отметили в selectedServices
        const newActiveServices = services.reduce<number[]>((res, cur) => {
            if (selectedServices.includes(cur.id)) {
                return [...res, cur.id];
            }
            return res;
        }, []);

        // если каких-то нет, то убираем их из selectedServices
        if (newActiveServices.length !== selectedServices.length) {
            setSelectedServices(newActiveServices);
        }

        // идем проверять какие сервисы нам доступны по выбранным
        const compatible = await getCompatibleServices(newActiveServices);

        setActiveServices(compatible.data.data.filter((d) => d.attributes.is_bookable).map((d) => Number(d.id)));

        setIsLoading(false);
    }, [selectedServices, params, getCompatibleServices, setSelectedServices]);

    const updateCompatible = useCallback(async () => {
        setIsLoading(true);
        const compatible = await getCompatibleServices();

        setActiveServices(compatible.data.data.filter((d) => d.attributes.is_bookable).map((d) => Number(d.id)));
        setIsLoading(false);
    }, [getCompatibleServices]);

    useEffect(() => {
        if (active) {
            // всегда при переходе на страницу апаем состояние сервисов
            updateAwailableState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, params]);

    useEffect(() => {
        if (active) {
            // если уже на странице и изменили выбранные услуги - обновим доступность
            updateCompatible();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedServices]);

    const resetServices = useCallback(() => {
        setServices([]);
        setCategories([]);
        setActiveServices([]);
    }, []);

    return {
        isLoading,
        services,
        categories,
        activeServices,
        resetServices,
    };
};
